/**
 * Production API Implementation of IUnitRepository
 * Decouples unit inventory transport from UI and state hooks.
 */
import api from '../../../lib/api';
import { dataUtils } from '../../../lib/dataUtils';
import { Unit, UnitConfigurationType, UnitSellingStatus, ResidentType } from '../types/unit';
import { ApiPageResponse } from '../../../types';
import { IUnitRepository } from './IUnitRepository';

export class ApiUnitRepository implements IUnitRepository {
  async getAll(signal?: AbortSignal): Promise<Unit[]> {
    const response = await api.get('/units?size=2000', { signal });
    const data = response.data;
    const content = data.content || data;
    return dataUtils.normalizeLookup<Unit>(content, 'unitId', 'unitName');
  }

  async getLookupBySociety(societyId: number, signal?: AbortSignal): Promise<Unit[]> {
    const response = await api.get(`/units/lookup/${societyId}`, { signal });
    return dataUtils.normalizeLookup<Unit>(response.data, 'unitId', 'unitName');
  }

  async getBySociety(societyId: number, page = 0, size = 10, signal?: AbortSignal): Promise<ApiPageResponse<Unit>> {
    const response = await api.get(`/units/society/${societyId}`, {
      params: { page, size },
      signal,
    });
    const data = response.data;
    if (data.content) {
      data.content = dataUtils.normalizeLookup<Unit>(data.content, 'unitId', 'unitName');
    }
    return data;
  }

  async getConfigurationTypes(signal?: AbortSignal): Promise<UnitConfigurationType[]> {
    const response = await api.get('/units/configurationTypes', { signal });
    return dataUtils.normalizeLookup<UnitConfigurationType>(response.data, 'unitConfigurationTypeId', 'typeName');
  }

  async getSellingStatuses(signal?: AbortSignal): Promise<UnitSellingStatus[]> {
    const response = await api.get('/units/sellingStatuses', { signal });
    return dataUtils.normalizeLookup<UnitSellingStatus>(response.data, 'unitSellingStatusId', 'typeName');
  }

  async getRoles(signal?: AbortSignal): Promise<any[]> {
    const response = await api.get('/lookups/roles', { signal });
    return dataUtils.normalizeLookup<any>(response.data, 'roleTypeId', 'roleName');
  }

  async getResidentTypes(signal?: AbortSignal): Promise<ResidentType[]> {
    const response = await api.get('/members/residentTypes', { signal });
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  }

  async getSocietyMembers(societyId: number, typeCodes: string, signal?: AbortSignal): Promise<any[]> {
    const response = await api.get(`/societies/${societyId}/members?residentTypeCodes=${typeCodes}&size=2000`, { signal });
    return dataUtils.extractData<any>(response.data);
  }

  async getById(unitId: number, signal?: AbortSignal): Promise<Unit> {
    const response = await api.get(`/units/${unitId}`, { signal });
    return dataUtils.normalizeItem<Unit>(response.data, 'unitId', 'unitName');
  }

  async create(unit: Unit, signal?: AbortSignal): Promise<Unit> {
    const response = await api.post('/units', unit, { signal });
    return dataUtils.normalizeItem<Unit>(response.data, 'unitId', 'unitName');
  }

  async update(unitId: number, unit: Unit, signal?: AbortSignal): Promise<Unit> {
    const response = await api.put(`/units/${unitId}`, unit, { signal });
    return dataUtils.normalizeItem<Unit>(response.data, 'unitId', 'unitName');
  }

  async delete(unitId: number, signal?: AbortSignal): Promise<void> {
    await api.delete(`/units/${unitId}`, { signal });
  }
}
